import { api } from '../api';
import { logError } from '../../utils/logger';
import { authService } from '../authService';

export interface ProntuarioDetalhado {
  id: number;
  consultaId: number;
  pacienteId: number;
  usuarioId: number;
  historicoSessao: string;
  observacoes: string;
  encaminhamentos: string;
  planoTratamento: string;
  evolucao: string;
  hipoteseDiagnostica: string;
  queixas: Array<{ id: number; valor: string; observacao?: string; intensidade?: number }>;
  sintomas: Array<{ id: number; valor: string; observacao?: string; intensidade?: number; frequencia?: string }>;
  humor: Array<{ id: number; valor: string; observacao?: string; predominante: boolean }>;
  medicamentos: Array<{ id: number; nome_medicamento: string; categoria: string; dosagem: string; posologia: string; observacao: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface DadosProntuario {
  pacienteId: number;
  usuarioId: number;
  historicoSessao?: string;
  observacoes?: string;
  encaminhamentos?: string;
  planoTratamento?: string;
  evolucao?: string;
  hipoteseDiagnostica?: string;
  queixas: number[];
  sintomas: number[];
  humor: number[];
}

export const prontuariosService = {
  buscarPorConsulta: async (consultaId: string): Promise<ProntuarioDetalhado | null> => {
    try {
      // Se for um ID especial de paciente, buscar prontuário geral
      if (consultaId.startsWith('paciente-')) {
        const pacienteId = consultaId.replace('paciente-', '');
        try {
          const response = await api.get<ProntuarioDetalhado>(`/prontuarios/paciente/${pacienteId}/geral`);
          return response.data;
        } catch (error: any) {
          if (error.response?.status === 404) {
            return null; // Prontuário geral não existe ainda
          }
          throw error;
        }
      }
      
      const response = await api.get<ProntuarioDetalhado>(`/prontuarios/consulta/${consultaId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // Prontuário não existe ainda
      }
      logError('ProntuariosService', 'Erro ao buscar prontuário', error);
      throw new Error('Falha ao carregar prontuário');
    }
  },

  salvar: async (consultaId: string, dados: DadosProntuario): Promise<ProntuarioDetalhado> => {
    try {
      // CORREÇÃO CRÍTICA: Garantir que usuarioId seja o do usuário autenticado
      const usuarioAtual = authService.getUsuario();
      if (!usuarioAtual) {
        throw new Error('Usuário não autenticado');
      }

      // Criar uma cópia dos dados com o ID do usuário correto
      const dadosComUsuarioCorreto: DadosProntuario = {
        ...dados,
        usuarioId: Number(usuarioAtual.id)
      };
      
      // Se for um ID especial de paciente, usar endpoint específico
      if (consultaId.startsWith('paciente-')) {
        const pacienteId = consultaId.replace('paciente-', '');
        const response = await api.post<{prontuario: ProntuarioDetalhado}>(
          `/prontuarios/paciente/${pacienteId}/geral`, 
          dadosComUsuarioCorreto
        );
        return response.data.prontuario;
      }
      
      // Validar dados antes de enviar
      if (!dadosComUsuarioCorreto.pacienteId) {
        throw new Error('ID do paciente não fornecido');
      }
      
      // Fazer a requisição ao backend com os dados corretos
      const response = await api.post<{prontuario: ProntuarioDetalhado}>(
        `/prontuarios/consulta/${consultaId}`, 
        dadosComUsuarioCorreto
      );
      
      return response.data.prontuario;
    } catch (error: any) {
      // Melhorar tratamento de erros
      let mensagemErro = 'Falha ao salvar prontuário';
      
      if (error.response?.data?.mensagem) {
        mensagemErro = error.response.data.mensagem;
      } else if (error.response?.data?.erro) {
        mensagemErro = error.response.data.erro;
      } else if (error.message) {
        mensagemErro = error.message;
      }
      
      // Log mais detalhado para diagnóstico
      logError('ProntuariosService', `Erro ao salvar prontuário: ${mensagemErro}`, error);
      
      throw new Error(mensagemErro);
    }
  },

  buscarCatalogo: async (tipo: 'queixas' | 'sintomas' | 'humor'): Promise<Array<{id: number, valor: string}>> => {
    try {
      const response = await api.get<{[key: string]: Array<{id: number, valor: string}>}>(`/prontuarios/catalogo/${tipo}`);
      return response.data[tipo] || [];
    } catch (error) {
      logError('ProntuariosService', `Erro ao buscar catálogo ${tipo}`, error);
      throw new Error(`Falha ao carregar catálogo ${tipo}`);
    }
  }
};
